import { type BufferGeometry, EdgesGeometry, Matrix4 } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import {
    calculateFullQuaternion,
    isPlacementIndicator
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { Article } from 'app/modules/wallforming/components/scenes/objects/articles/types/article';
import type { ArticleEdgeGeometries } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleEdgeGeometries';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';

const singlePartEdgesCache = new Map<string, EdgesGeometry>();
const multiPartEdgesCache = new Map<string, EdgesGeometry>();

export function removeCachedInstanceEdges(instanceIds: string[]): void {
    if (instanceIds.length === 0 || multiPartEdgesCache.size === 0) {
        return;
    }

    const invalidatedInstanceIds = new Set(instanceIds);
    for (const key of multiPartEdgesCache.keys()) {
        const separatorIndex = key.indexOf(':');
        const keyInstanceId = separatorIndex >= 0 ? key.slice(0, separatorIndex) : key;
        if (invalidatedInstanceIds.has(keyInstanceId)) {
            multiPartEdgesCache.delete(key);
        }
    }
}

function getPartGeometryForLod(
    edgeGeometries: ArticleEdgeGeometries,
    partName: string,
    lodLevel: number
): BufferGeometry | null {
    return edgeGeometries[partName]?.[lodLevel] ?? null;
}

function getAvailableGeometryForLod(edgeGeometries: ArticleEdgeGeometries, lodLevel: number): BufferGeometry | null {
    for (const geometries of Object.values(edgeGeometries)) {
        const geometry = geometries[lodLevel] ?? geometries.find((entry): entry is BufferGeometry => entry !== null);
        if (geometry) {
            return geometry;
        }
    }

    return null;
}

function applyArticlePartTransform(
    geometry: BufferGeometry,
    part: ArticleInstance['parts'][string],
    tmpMatrix: Matrix4
): BufferGeometry {
    const quaternion = calculateFullQuaternion(part.alignment, part.rotation);
    tmpMatrix.compose(part.position, quaternion, part.scale);
    geometry.applyMatrix4(tmpMatrix);

    return geometry;
}

export function updateSinglePartInstanceEdges(
    article: Article,
    edgesArray: BufferGeometry[],
    tmpMatrix: Matrix4,
    getInstanceLodLevel: (instanceId: string, instance: ArticleInstance) => number
): void {
    const state = usePlacementStore.getState();
    const edgeGeometries = article.edgeGeometries!;

    Object.entries(article.articleInstances).forEach(([instanceId, instance]) => {
        // Skip temporary placement indicator instances
        if (isPlacementIndicator(instanceId, state)) {
            return;
        }

        const lodLevel = getInstanceLodLevel(instanceId, instance);
        const fallbackGeometry = getAvailableGeometryForLod(edgeGeometries, lodLevel);
        if (!fallbackGeometry) {
            return;
        }

        const cacheKey = `${article.articleNumber}:${lodLevel}`;
        let cached = singlePartEdgesCache.get(cacheKey);
        if (!cached) {
            cached = new EdgesGeometry(fallbackGeometry, 1);
            singlePartEdgesCache.set(cacheKey, cached);
        }

        Object.values(instance.parts).forEach((part) => {
            const cloned = cached.clone() as EdgesGeometry;
            edgesArray.push(applyArticlePartTransform(cloned, part, tmpMatrix));
        });
    });
}

export function updateMultiPartInstanceEdges(
    instanceId: string,
    instance: ArticleInstance,
    edgeGeometries: ArticleEdgeGeometries,
    edgesArray: BufferGeometry[],
    tmpMatrix: Matrix4,
    lodLevel: number
): void {
    const state = usePlacementStore.getState();

    // Skip temporary placement indicator instances
    if (isPlacementIndicator(instanceId, state)) {
        return;
    }

    const cacheKey = `${instanceId}:${lodLevel}`;
    const cached = multiPartEdgesCache.get(cacheKey);
    if (cached) {
        const cloned = cached.clone() as EdgesGeometry;
        edgesArray.push(cloned);
        return;
    }

    const transformedGeometries: BufferGeometry[] = [];
    Object.entries(instance.parts).forEach(([partName, part]) => {
        const exactPartGeometry = getPartGeometryForLod(edgeGeometries, partName, lodLevel);
        const partGeometry = exactPartGeometry ?? getAvailableGeometryForLod(edgeGeometries, lodLevel);
        if (!partGeometry) {
            return;
        }

        const cloned = partGeometry.clone();
        if (exactPartGeometry) {
            const { position, rotation, alignment, scale: partScale } = part;
            const quaternion = calculateFullQuaternion(alignment, rotation);
            tmpMatrix.compose(position, quaternion, partScale);
            cloned.applyMatrix4(tmpMatrix);
            transformedGeometries.push(cloned);
            return;
        }

        transformedGeometries.push(applyArticlePartTransform(cloned, part, tmpMatrix));
    });

    if (transformedGeometries.length === 0) {
        return;
    }

    const instanceGeometry =
        transformedGeometries.length === 1 ? transformedGeometries[0] : mergeGeometries(transformedGeometries);

    if (instanceGeometry) {
        const edges = new EdgesGeometry(instanceGeometry, 1);
        multiPartEdgesCache.set(cacheKey, edges);
        edgesArray.push(edges);
    }
}
